import {defineStore} from 'pinia'
import {ref, computed, watch} from 'vue'
import type {Product, ProductPackaging} from '~/types/product'
import {getEffectivePrice, is19lPackaging, isGallonTypeCat} from '~/utils/products';
import {depositPer19lBottle} from '~/constants/delivery';
import {computeDeliveryInfo, type DeliveryInfo} from '~/utils/delivery';
import {track} from '~/utils/tracking';
import {cartItemToItem, ecommerceFor} from '~/utils/tracking-items';

export interface CartItem {
    id: number
    name: string
    /** Bulgarian name and brand for analytics. Missing on carts saved before the fields existed. */
    nameBg?: string | null
    brandName?: string | null
    discountPercent?: number | null
    /** Neosoft product code. Missing on carts saved before the field existed. */
    code?: string | null
    quantity: number
    price: number
    image: string | null
    slug?: string
    categorySlug?: string
    norm: number
    typeCatName?: string | null
    packaging?: readonly ProductPackaging[]
}

export const useCartStore = defineStore('cart', () => {
    const items = ref<CartItem[]>([])

    const totalPrice = computed(() => items.value.reduce((sum, it) => sum + it.price * it.quantity, 0))

    /** `quantity` is the real change in charged units (new minus old). */
    function trackQuantityChange(item: CartItem, quantity: number) {
        if (quantity === 0) return
        const lineItem = cartItemToItem(item, Math.abs(quantity))
        track(quantity > 0 ? 'add_to_cart' : 'remove_from_cart', {}, { ecommerce: ecommerceFor([lineItem]) })
    }

    function addProduct(product: Product, categorySlug?: string) {
        const norm = product.norm || 1
        const existing = items.value.find((i) => i.id === product.id)
        const price = getEffectivePrice(product)
        const image = product.imageUrls && product.imageUrls.length > 0 ? product.imageUrls[0]!.imageUrl : null
        const brandName = product.brandName ?? product.brand?.name ?? null

        if (existing) {
            existing.quantity += norm
            if (categorySlug && !existing.categorySlug) {
                existing.categorySlug = categorySlug
            }
            if (!existing.code && product.code) {
                existing.code = product.code
            }
            if (!existing.nameBg && product.nameBg) existing.nameBg = product.nameBg
            if (!existing.brandName && brandName) existing.brandName = brandName
            existing.discountPercent = product.discountPercent ?? null
            trackQuantityChange(existing, norm)
        } else {
            const item: CartItem = {
                id: product.id,
                name: product.name,
                nameBg: product.nameBg ?? null,
                brandName,
                discountPercent: product.discountPercent ?? null,
                code: product.code,
                quantity: norm,
                price,
                image,
                slug: product.slug,
                categorySlug,
                norm,
                typeCatName: product.typeCatName,
                packaging: product.packaging,
            }
            items.value.push(item)
            trackQuantityChange(item, norm)
        }
    }

    function addCartItems(newItems: CartItem[], append: boolean = true) {
        if (!append) {
            items.value = []
        }
        for (const item of newItems) {
            const existing = items.value.find((i) => i.id === item.id)
            if (existing) {
                existing.quantity += item.quantity
                if (!existing.code && item.code) {
                    existing.code = item.code
                }
            } else {
                items.value.push({ ...item })
            }
        }
    }

    function removeProduct(id: number) {
        const removed = items.value.find((i) => i.id === id)
        items.value = items.value.filter((i) => i.id !== id)
        if (removed) trackQuantityChange(removed, -removed.quantity)
    }

    function setQuantity(id: number, qty: number) {
        const it = items.value.find((i) => i.id === id)
        if (it) it.quantity = qty
    }

    function changeQuantity(id: number, delta: number) {
        const it = items.value.find(i => i.id === id)
        if (!it) return
        const norm = it.norm || 1
        const oldQty = it.quantity
        const newQty = Math.max(norm, it.quantity + delta * norm)
        setQuantity(id, newQty)
        trackQuantityChange(it, newQty - oldQty)
    }

    function getQuantity(id: number): number {
        return items.value.find((i) => i.id === id)?.quantity ?? 0
    }

    function getDisplayQuantity(id: number): number {
        const it = items.value.find((i) => i.id === id)
        if (!it) return 0
        return it.quantity / (it.norm || 1)
    }

    function getItemPrice(id: number): number {
        const it = items.value.find((i) => i.id === id)
        if (!it) return 0
        return it.price * it.quantity
    }

    const totalCount = computed(() => items.value.length)

    function clear() {
        items.value = []
    }

    const countOf19lPackaging = computed(() => {
        return items.value.reduce((sum, item) => {
            if (!is19lPackaging(item.packaging)) return sum
            return sum + getDisplayQuantity(item.id)
        }, 0)
    })

    const gallonQty = computed(() => {
        return items.value.reduce((sum, item) => {
            if (!isGallonTypeCat(item.typeCatName || '')) return sum
            return sum + getDisplayQuantity(item.id)
        }, 0)
    })
    
    const deliveryInfo = computed<DeliveryInfo>(() =>
        computeDeliveryInfo(gallonQty.value, totalPrice.value)
    )

    const returnBottles = ref<{ vodi4ka: number; other: number }>({ vodi4ka: 0, other: 0 })

    const bottlesFromOthers = computed(() => {
        return returnBottles.value.other;
    })

    const totalBottlesToReturn = computed(() =>
        returnBottles.value.vodi4ka + returnBottles.value.other
    )

    const depositAmount = computed(() =>
        Math.max(0, countOf19lPackaging.value - totalBottlesToReturn.value) * depositPer19lBottle
    )

    function resetReturnBottles() {
        returnBottles.value.vodi4ka = 0
        returnBottles.value.other = 0
    }

    watch(countOf19lPackaging, resetReturnBottles)

    return {
        items,
        totalPrice,
        totalCount,
        addProduct,
        addCartItems,
        removeProduct,
        changeQuantity,
        getQuantity,
        getDisplayQuantity,
        getItemPrice,
        clear,
        deliveryInfo,
        gallonQty,
        countOf19lPackaging,
        returnBottles,
        totalBottlesToReturn,
        depositAmount,
        resetReturnBottles,
        bottlesFromOthers
    }
}, {persist: {omit: ['returnBottles']}})
